import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Paper,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Plus,
  PencilSimple,
  Trash,
  DotsThreeVertical,
  FileText
} from '@phosphor-icons/react';
import api from '../../../utils/api';
import { useBusiness } from '../../../context/BusinessContext';

const NotesEditor = ({ leadId, onNoteAdded, onNoteUpdated, onNoteDeleted }) => {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.business?._id;
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNote, setSelectedNote] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editNoteTitle, setEditNoteTitle] = useState('');
  const [editNoteContent, setEditNoteContent] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuNote, setMenuNote] = useState(null);

  useEffect(() => {
    fetchNotes();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [leadId, businessId]);

  const fetchNotes = async () => {
    if (!businessId) return;
    try {
      setLoading(true);
      const response = await api.get(`/api/leads/${leadId}/notes`, { params: { businessId } });
      setNotes(response.data);
    } catch (error) {} finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async () => {
    if (!businessId) return;
    try {
      const response = await api.post(`/api/leads/${leadId}/notes`, {
        businessId,
        title: newNoteTitle,
        content: newNoteContent
      });
      
      setNotes(prev => [response.data, ...prev]);
      setNewNoteTitle('');
      setNewNoteContent('');
      setShowCreateDialog(false);
      window.dispatchEvent(new Event('supplier-onboarding-progress-changed'));
      
      if (onNoteAdded) {
        onNoteAdded(response.data);
      }
    } catch (error) {}
  };

  const handleUpdateNote = async () => {
    if (!businessId) return;
    try {
      const response = await api.put(`/api/leads/${leadId}/notes/${selectedNote._id}`, {
        businessId,
        title: editNoteTitle,
        content: editNoteContent
      });
      
      setNotes(prev => prev.map(note => 
        note._id === selectedNote._id ? response.data : note
      ));
      setShowEditDialog(false);
      setSelectedNote(null);
      
      if (onNoteUpdated) {
        onNoteUpdated(response.data);
      }
    } catch (error) {}
  };

  const handleDeleteNote = async (noteId) => {
    if (!businessId) return;
    try {
      await api.delete(`/api/leads/${leadId}/notes/${noteId}`, { params: { businessId } });
      setNotes(prev => prev.filter(note => note._id !== noteId));
      
      if (onNoteDeleted) {
        onNoteDeleted(noteId);
      }
    } catch (error) {}
  };

  const handleMenuClick = (event, note) => {
    setAnchorEl(event.currentTarget);
    setMenuNote(note);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuNote(null);
  };

  const handleEditClick = (note) => {
    setSelectedNote(note);
    setEditNoteTitle(note.title);
    setEditNoteContent(note.content);
    setShowEditDialog(true);
    handleMenuClose();
  };

  const handleDeleteClick = (note) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      handleDeleteNote(note._id);
    }
    handleMenuClose();
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography>Loading notes...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Notes ({notes.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={20} />}
          onClick={() => setShowCreateDialog(true)}
        >
          New Note
        </Button>
      </Box>

      {/* Notes List */}
      {notes.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <FileText size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No notes yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Add a note to capture what matters for this couple.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Plus size={20} />}
            onClick={() => setShowCreateDialog(true)}
          >
            Create First Note
          </Button>
        </Paper>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {notes.map((note) => (
            <Paper
              key={note._id}
              elevation={0}
              sx={{
                bgcolor: 'background.paper',
                p: 2,
                borderRadius: 1
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Box flexGrow={1}>
                  <Typography variant="h6" gutterBottom component="div">
                    {note.title}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2} mt={0.5}>
                    {note.isPinned && (
                      <Chip
                        label="Pinned"
                        size="small"
                        color="primary"
                      />
                    )}
                  </Box>
                </Box>
                <IconButton
                  onClick={(e) => handleMenuClick(e, note)}
                  size="small"
                >
                  <DotsThreeVertical size={20} />
                </IconButton>
              </Box>
              
              <Typography 
                variant="body2" 
                color="text.primary"
                component="div"
                sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  mb: 1
                }}
              >
                {note.content || 'No content'}
              </Typography>

              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ fontSize: '0.75rem' }}>
                  {note.createdBy?.firstName && note.createdBy?.lastName
                    ? `${note.createdBy.firstName} ${note.createdBy.lastName}`
                    : note.createdBy?.firstName || 'Unknown'
                  } created this note on {formatDate(note.createdAt)}
                </Typography>
                {note.editedBy && note.updatedAt && 
                 new Date(note.updatedAt).getTime() !== new Date(note.createdAt).getTime() && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }} component="div">
                    Last edited by{' '}
                    {note.editedBy?.firstName && note.editedBy?.lastName
                      ? `${note.editedBy.firstName} ${note.editedBy.lastName}`
                      : note.editedBy?.firstName || 'Unknown'
                    } on {formatDate(note.updatedAt)}
                  </Typography>
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Create Note Dialog */}
      <Dialog 
        open={showCreateDialog} 
        onClose={() => setShowCreateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Note Title"
            fullWidth
            variant="outlined"
            value={newNoteTitle}
            onChange={(e) => setNewNoteTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Content"
            fullWidth
            multiline
            rows={6}
            value={newNoteContent}
            onChange={(e) => setNewNoteContent(e.target.value)}
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateNote} 
            variant="contained"
            disabled={!newNoteTitle.trim()}
          >
            Create Note
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Note Dialog */}
      <Dialog 
        open={showEditDialog} 
        onClose={() => setShowEditDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Note Title"
            fullWidth
            variant="outlined"
            value={editNoteTitle}
            onChange={(e) => setEditNoteTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Content"
            fullWidth
            multiline
            rows={6}
            value={editNoteContent}
            onChange={(e) => setEditNoteContent(e.target.value)}
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateNote} 
            variant="contained"
            disabled={!editNoteTitle.trim()}
          >
            Update Note
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditClick(menuNote)}>
          <PencilSimple size={20} style={{ marginRight: 8 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleDeleteClick(menuNote)} sx={{ color: 'error.main' }}>
          <Trash size={20} style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default NotesEditor;
